/**
 * 顶部导航（Logo、主菜单、快捷操作、用户区，含角色菜单）
 * - 主菜单 5 项；竞价进行中显示橙色圆点
 * - 「我要卖牛」高亮主按钮；未登录点击跳登录并回跳
 * - 用户区按角色显示经纪工作台 / 发布挂牌入口
 */
import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/stores/auth';
import { listAuctions } from '@/api/auction';
import { USER_TYPE } from '@/utils/constants';

const NAV_ITEMS = [
  { to: '/', label: '交易大厅' },
  { to: '/market', label: '牛只市场' },
  { to: '/auctions', label: '竞价大厅' },
  { to: '/quotes', label: '行情看板' },
];

export default function Header() {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [liveAuction, setLiveAuction] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [unread] = useState(2);

  // 竞价进行中 → 菜单橙色圆点
  useEffect(() => {
    let mounted = true;
    listAuctions(1)
      .then((list) => {
        if (mounted) setLiveAuction(Array.isArray(list) && list.length > 0);
      })
      .catch(() => {
        if (mounted) setLiveAuction(false);
      });
    return () => {
      mounted = false;
    };
  }, [location.pathname]);

  const goSell = () => {
    if (!isAuthenticated) {
      navigate(`/login?redirect=${encodeURIComponent('/listing/new')}`);
      return;
    }
    navigate('/listing/new');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const userName = user?.real_name || user?.username || '用户';
  const isBroker = user?.roles?.includes('broker');

  return (
    <header className="header">
      <div className="container header-inner">
        <Link className="header-brand" to="/">
          <img src="/logo.svg" onError={(e) => { (e.target as HTMLImageElement).src = "/logo.png"; }} alt="" className="brand-logo" />
          <span>
            <span className="brand-name">云牛集 · 牛只交易平台</span>
            <br />
            <span className="brand-sub">山东牛产业智慧平台</span>
          </span>
        </Link>

        <nav className="header-nav">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            >
              {item.label}
              {item.to === '/auctions' && liveAuction ? <span className="live-dot" /> : null}
            </NavLink>
          ))}
          <div className="nav-group">
            <span className="nav-link">经纪撮合 ▾</span>
            <div className="nav-group-menu">
              <Link to="/broker/orders/new">我要委托</Link>
              <Link to="/broker/orders">委托大厅</Link>
              <Link to="/broker/workbench">经纪工作台</Link>
            </div>
          </div>
        </nav>

        <div className="header-actions">
          <button className="btn btn-sm sell-btn" onClick={goSell}>
            ＋ <span>我要卖牛</span>
          </button>

          {isAuthenticated ? (
            <div className="header-user">
              <Link className="user-link" to="/orders">我的订单</Link>
              <button className="bell" title="消息通知" onClick={() => navigate('/my?tab=notify')}>
                🔔
                {unread > 0 ? <span className="badge">{unread}</span> : null}
              </button>
              <div className="user-menu">
                <div className="menu-name">
                  {userName} · {USER_TYPE[user?.user_type ?? 4] ?? '用户'}
                </div>
                <Link to="/my">个人中心</Link>
                <Link to="/my/listings">我的挂牌</Link>
                <Link to="/my/wallet">我的钱包</Link>
                <Link to="/my/enterprise">企业入驻</Link>
                {isBroker ? <Link to="/broker/workbench">经纪工作台</Link> : null}
                <button className="menu-logout" onClick={handleLogout}>退出登录</button>
              </div>
              <Link className="user-link" to="/my" aria-label="个人中心">
                <span className="avatar">{userName.slice(0, 1)}</span>
              </Link>
            </div>
          ) : (
            <div className="header-user">
              <Link className="user-link" to="/login">登录</Link>
              <Link className="user-link" to="/register">注册</Link>
            </div>
          )}
        </div>

        <button className="header-burger" onClick={() => setDrawerOpen(true)} aria-label="打开菜单">
          ☰
        </button>
      </div>

      {drawerOpen ? (
        <>
          <div className="drawer-mask" onClick={() => setDrawerOpen(false)} />
          <div className="drawer-panel open">
            <div className="drawer-title">云牛集 · 交易平台</div>
            {NAV_ITEMS.map((item) => (
              <Link key={item.to} to={item.to} onClick={() => setDrawerOpen(false)}>
                {item.label}
              </Link>
            ))}
            <Link to="/broker/orders/new" onClick={() => setDrawerOpen(false)}>经纪委托</Link>
            <Link to="/broker/workbench" onClick={() => setDrawerOpen(false)}>经纪工作台</Link>
            <Link to="/listing/new" onClick={() => setDrawerOpen(false)}>发布挂牌</Link>
            <Link to="/orders" onClick={() => setDrawerOpen(false)}>我的订单</Link>
            <Link to="/my" onClick={() => setDrawerOpen(false)}>个人中心</Link>
            <Link to="/my/wallet" onClick={() => setDrawerOpen(false)}>我的钱包</Link>
            {isAuthenticated ? (
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleLogout();
                  setDrawerOpen(false);
                }}
              >
                退出登录
              </a>
            ) : (
              <>
                <Link to="/login" onClick={() => setDrawerOpen(false)}>登录</Link>
                <Link to="/register" onClick={() => setDrawerOpen(false)}>注册</Link>
              </>
            )}
          </div>
        </>
      ) : null}
    </header>
  );
}
